/**
 * Project Service
 *
 * Reads portfolio-project content from the real backend API
 * (/api/v1/portfolio/projects) instead of the static src/data/projects.json
 * this used to read from - the backend already had a complete table + CRUD
 * API for this content, wired only to the admin panel; nothing on the
 * public site ever called it. Keeps the exact same public method surface
 * (getAllProjects/getFeaturedProjects/getProjectById) so ProjectPage,
 * WelcomePage, and ProjectPopup need no changes.
 */

import { apiService } from './apiService';
import type { Project } from '@/shared/types';
import { ProjectStatus, ProjectCategory } from '@/shared/types';

// Shape of a row as returned by PortfolioProject.to_dict() /
// PortfolioProjectResponse.to_dict() on the backend.
interface BackendPortfolioProject {
  id: string;
  title: string;
  description: string | null;
  short_description: string | null;
  image_url: string | null;
  thumbnail_url: string | null;
  project_url: string | null;
  github_url: string | null;
  status: string; // 'active' | 'completed' | 'archived'
  featured: boolean;
  order_index: number;
  tags: string[];
  technologies: string[];
  category: string | null;
  project_metadata: {
    legacy_id?: number;
    client?: string;
    completion_date_display?: string;
    details?: { overview: string[]; solution: string[]; results: string[] };
  } | null;
  created_at: string | null;
  updated_at: string | null;
}

interface BackendProjectListData {
  projects: BackendPortfolioProject[];
  total: number;
  page: number;
  per_page: number;
}

// Reverses the mapping applied when this content was seeded from the old
// static JSON (docs/migrations/seed_hardcoded_portfolio_projects.sql):
// 'live' -> 'active', 'inactive' -> 'archived'. ProjectPopup does a literal
// string check against 'live'/'inactive' for its status badge, so this has
// to land on those exact values, not just "a" ProjectStatus.
const backendStatusToProjectStatus = (status: string): ProjectStatus => {
  switch (status) {
    case 'active':
      return ProjectStatus.LIVE;
    case 'archived':
      return ProjectStatus.INACTIVE;
    case 'completed':
      return ProjectStatus.COMPLETED;
    default:
      return ProjectStatus.COMPLETED;
  }
};

const backendCategoryToProjectCategory = (category: string | null): ProjectCategory | undefined => {
  if (!category) return undefined;
  const match = Object.values(ProjectCategory).find((c) => c === category);
  return match as ProjectCategory | undefined;
};

const transformProject = (row: BackendPortfolioProject): Project => {
  const metadata = row.project_metadata || {};
  return {
    id: row.id,
    title: row.title,
    description: row.description || row.short_description || '',
    longDescription: row.description || undefined,
    image: row.image_url || undefined,
    tags: row.tags || [],
    status: backendStatusToProjectStatus(row.status),
    client: metadata.client,
    startDate: undefined,
    completionDate: metadata.completion_date_display,
    githubUrl: row.github_url || undefined,
    liveUrl: row.project_url || undefined,
    link: row.project_url || undefined,
    featured: row.featured,
    technologies: row.technologies || [],
    category: backendCategoryToProjectCategory(row.category),
    createdAt: row.created_at || new Date().toISOString(),
    updatedAt: row.updated_at || new Date().toISOString(),
    details: metadata.details,
  };
};

// The old static data used small numeric ids (201-204) in shareable URLs;
// real rows use UUIDs. The seed migration kept the original id as
// project_metadata.legacy_id specifically so old links keep resolving -
// this checks that fallback whenever the requested id isn't a UUID.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

class ProjectServiceClass {
  private async fetchAll(params?: { featured?: boolean }): Promise<BackendPortfolioProject[]> {
    const query = new URLSearchParams({ per_page: '100' });
    if (params?.featured) query.set('featured', 'true');
    const response = await apiService.get<BackendProjectListData>(`/portfolio/projects?${query.toString()}`);
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to load portfolio projects');
    }
    return [...response.data.projects].sort((a, b) => a.order_index - b.order_index);
  }

  async getAllProjects(): Promise<Project[]> {
    const rows = await this.fetchAll();
    return rows.map(transformProject);
  }

  async getFeaturedProjects(): Promise<Project[]> {
    const rows = await this.fetchAll({ featured: true });
    return rows.map(transformProject);
  }

  async getProjectById(id: string): Promise<Project | null> {
    if (UUID_RE.test(id)) {
      const response = await apiService.get<BackendPortfolioProject>(`/portfolio/projects/${id}`);
      if (response.success && response.data) {
        return transformProject(response.data);
      }
      return null;
    }
    // Legacy numeric id (old static-JSON links) - resolve via project_metadata.
    const rows = await this.fetchAll();
    const match = rows.find((row) => String(row.project_metadata?.legacy_id ?? '') === id);
    return match ? transformProject(match) : null;
  }
}

// Export singleton instance
export const ProjectService = new ProjectServiceClass();
export default ProjectService;

// Export type for compatibility
export type { Project };
